import type {
	HeatmapClickBatch,
	HeatmapClickPoint,
	HeatmapConsentCategory,
	HeatmapDefaultScope,
	HeatmapEntryOverride,
	HeatmapTrackerStatus,
	HeatmapTrackingConfig,
} from "../../types/src/seo";

type UnknownRecord = Record<string, unknown>;

const PATH_PATTERN = /^\/[A-Za-z0-9/_\-.:~%]*$/;
const ENTRY_ID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const DEFAULT_HEATMAP_TRACKING_CONFIG: HeatmapTrackingConfig = {
	batch_size: 20,
	consent_category: "analytics",
	default_scope: "off",
	enabled: false,
	endpoint_path: "/api/heatmap/clicks",
	flush_interval_ms: 4000,
	max_clicks_per_request: 50,
	require_cookie_consent: true,
	sample_rate: 1,
};

function asObject(value: unknown): UnknownRecord {
	return typeof value === "object" && value !== null && !Array.isArray(value)
		? (value as UnknownRecord)
		: {};
}

function asBoolean(value: unknown, fallback: boolean): boolean {
	return typeof value === "boolean" ? value : fallback;
}

function asPositiveInt(value: unknown, fallback: number): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return fallback;
	}
	const next = Math.trunc(value);
	return next > 0 ? next : fallback;
}

function asString(value: unknown, fallback: string): string {
	if (typeof value !== "string") {
		return fallback;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : fallback;
}

function asIsoString(value: unknown): string | null {
	if (typeof value !== "string" || value.trim().length === 0) {
		return null;
	}
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function ensureIsoString(value: string | undefined): string {
	return asIsoString(value) ?? new Date().toISOString();
}

function normalizeEndpointPath(value: unknown, fallback: string): string {
	const next = asString(value, fallback);
	return next.startsWith("/") ? next : fallback;
}

function normalizeSampleRate(value: unknown, fallback: number): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return fallback;
	}
	if (value < 0) {
		return 0;
	}
	if (value > 1) {
		return 1;
	}
	return value;
}

function normalizeDefaultScope(value: unknown): HeatmapDefaultScope {
	if (value === "all_entries" || value === "site") {
		return "all_entries";
	}
	return "off";
}

function normalizeConsentCategory(value: unknown): HeatmapConsentCategory {
	if (
		value === "preferences" ||
		value === "analytics" ||
		value === "marketing"
	) {
		return value;
	}
	return "analytics";
}

export function defaultHeatmapTrackingConfig(): HeatmapTrackingConfig {
	return structuredClone(DEFAULT_HEATMAP_TRACKING_CONFIG);
}

export function normalizeHeatmapEntryOverride(
	value: unknown
): HeatmapEntryOverride {
	if (value === "on" || value === "off" || value === "inherit") {
		return value;
	}
	return "inherit";
}

export function normalizeHeatmapTrackingConfig(
	raw: unknown
): HeatmapTrackingConfig {
	const object = asObject(raw);
	const fallback = defaultHeatmapTrackingConfig();
	return {
		batch_size: Math.min(
			asPositiveInt(object.batch_size ?? object.batchSize, fallback.batch_size),
			100
		),
		consent_category: normalizeConsentCategory(
			object.consent_category ?? object.consentCategory
		),
		default_scope: normalizeDefaultScope(
			object.default_scope ?? object.defaultScope ?? object.scope
		),
		enabled: asBoolean(object.enabled, fallback.enabled),
		endpoint_path: normalizeEndpointPath(
			object.endpoint_path ?? object.endpointPath,
			fallback.endpoint_path
		),
		flush_interval_ms: Math.min(
			asPositiveInt(
				object.flush_interval_ms ?? object.flushIntervalMs,
				fallback.flush_interval_ms
			),
			60_000
		),
		max_clicks_per_request: Math.min(
			asPositiveInt(
				object.max_clicks_per_request ?? object.maxClicksPerRequest,
				fallback.max_clicks_per_request
			),
			200
		),
		require_cookie_consent: asBoolean(
			object.require_cookie_consent ?? object.requireCookieConsent,
			fallback.require_cookie_consent
		),
		sample_rate: normalizeSampleRate(
			object.sample_rate ?? object.sampleRate,
			fallback.sample_rate
		),
	};
}

/**
 * Site `enabled` is the master switch. Entry `off` always wins.
 * Entry `on` collects even when `default_scope` is `off`.
 * Inherit follows `default_scope` (`all_entries` vs `off`).
 */
export function isHeatmapCollectionAllowed(params: {
	config: HeatmapTrackingConfig;
	entry_override?: HeatmapEntryOverride | null;
}): boolean {
	if (!params.config.enabled) {
		return false;
	}
	const override = normalizeHeatmapEntryOverride(params.entry_override);
	if (override === "off") {
		return false;
	}
	if (override === "on") {
		return true;
	}
	return params.config.default_scope === "all_entries";
}

export function resolveHeatmapTrackerStatus(params: {
	collection_allowed: boolean;
	config: HeatmapTrackingConfig;
	consent_allowed: boolean;
}): HeatmapTrackerStatus {
	if (!(params.config.enabled && params.collection_allowed)) {
		return "disabled";
	}
	return params.consent_allowed ? "ready" : "awaiting_consent";
}

export function shouldSampleHeatmapClick(
	sampleRate: number,
	randomValue = Math.random()
): boolean {
	if (sampleRate <= 0) {
		return false;
	}
	if (sampleRate >= 1) {
		return true;
	}
	return randomValue < sampleRate;
}

export function normalizeHeatmapClickPercent(value: number): number | null {
	if (!Number.isFinite(value)) {
		return null;
	}
	if (value < 0 || value > 100) {
		return null;
	}
	return Math.round(value * 100) / 100;
}

export function clickPositionToPercent(params: {
	clientX: number;
	clientY: number;
	scrollHeight: number;
	scrollWidth: number;
	scrollX: number;
	scrollY: number;
}): { x_pct: number; y_pct: number } | null {
	const width = params.scrollWidth;
	const height = params.scrollHeight;
	if (!(width > 0 && height > 0)) {
		return null;
	}
	const x = ((params.clientX + params.scrollX) / width) * 100;
	const y = ((params.clientY + params.scrollY) / height) * 100;
	const xPct = normalizeHeatmapClickPercent(Math.min(100, Math.max(0, x)));
	const yPct = normalizeHeatmapClickPercent(Math.min(100, Math.max(0, y)));
	if (xPct == null || yPct == null) {
		return null;
	}
	return { x_pct: xPct, y_pct: yPct };
}

export function normalizeHeatmapPath(value: unknown): string | null {
	if (typeof value !== "string") {
		return null;
	}
	const trimmed = value.trim();
	if (!PATH_PATTERN.test(trimmed) || trimmed.length > 1024) {
		return null;
	}
	return trimmed;
}

export function normalizeHeatmapEntryId(value: unknown): string | null {
	if (typeof value !== "string") {
		return null;
	}
	const trimmed = value.trim();
	return ENTRY_ID_PATTERN.test(trimmed) ? trimmed : null;
}

export function shapeHeatmapClickBatch(params: {
	clicks: HeatmapClickPoint[];
	config: HeatmapTrackingConfig;
	sent_at?: string;
	tracker_version?: string;
}): HeatmapClickBatch {
	const clicks: HeatmapClickPoint[] = [];
	for (const click of params.clicks) {
		const path = normalizeHeatmapPath(click.path);
		const xPct = normalizeHeatmapClickPercent(click.x_pct);
		const yPct = normalizeHeatmapClickPercent(click.y_pct);
		if (!(path && xPct != null && yPct != null)) {
			continue;
		}
		const viewportW = Math.trunc(click.viewport_w);
		const viewportH = Math.trunc(click.viewport_h);
		if (
			!(
				Number.isFinite(viewportW) &&
				Number.isFinite(viewportH) &&
				viewportW > 0 &&
				viewportH > 0 &&
				viewportW <= 20_000 &&
				viewportH <= 20_000
			)
		) {
			continue;
		}
		clicks.push({
			entry_id: normalizeHeatmapEntryId(click.entry_id),
			occurred_at: ensureIsoString(click.occurred_at),
			path,
			viewport_h: viewportH,
			viewport_w: viewportW,
			x_pct: xPct,
			y_pct: yPct,
		});
		if (clicks.length >= params.config.max_clicks_per_request) {
			break;
		}
	}
	return {
		clicks,
		sent_at: ensureIsoString(params.sent_at),
		tracker_version: params.tracker_version?.trim() || "v1",
	};
}
